import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
    makeStyles,
    Typography,
    Paper,
    Grid,
    Box,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select,
    MenuItem,
    IconButton,
    TextField,
    InputAdornment,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DeleteIcon from "@material-ui/icons/Delete";
import SearchIcon from "@material-ui/icons/Search";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";
import { modal } from "../../../util";
import PaginationBar from "../../common/PaginationBar";
import dayjs from "dayjs";

const useStyles = makeStyles((theme) => ({
  content: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  row: {
    marginBottom: theme.spacing(1),
  },
  label: {
    color: "#666",
    marginRight: theme.spacing(1),
    minWidth: 140,
    display: "inline-block",
  },
  codeList: {
    marginTop: theme.spacing(1),
  },
  chip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
  },
  tableCell: {
    padding: theme.spacing(1),
    fontSize: "0.875rem",
  },
  tableHeaderCell: {
    padding: theme.spacing(1),
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  promoCode: {
    fontFamily: "monospace",
    fontSize: "0.875rem",
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    margin: 0,
  },
  statusChip: {
    margin: theme.spacing(0.5),
  },
  statusSelect: {
    minWidth: 80,
    height: "32px",
    "& .MuiSelect-select": {
      padding: theme.spacing(0.5, 1),
      fontSize: "0.8125rem",
      paddingRight: theme.spacing(3),
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderWidth: "1px",
    },
  },
  deleteButton: {
    color: theme.palette.error.main,
    "&:disabled": {
      color: theme.palette.action.disabled,
    },
  },
  searchBox: {
    marginBottom: theme.spacing(2),
    maxWidth: 400,
  },
}));

const formatDate = (dt) => (dt ? dayjs(dt).format("DD MMM YYYY") : "-");

export default function CouponDetailsPage() {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const { setLoading } = useLoading();

  const [loading, setLoadingState] = useState(false);
  const [detail, setDetail] = useState(null);
  
  // Pagination state for promos table
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [paginatedPromos, setPaginatedPromos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setLoadingState(true);
    try {
      const res = await apiHelper.cineclub.getPromoTemplateById(id);
      if (res && res.data) {
        // Sort promos by ID in ascending order
        if (res.data.promos && Array.isArray(res.data.promos)) {
          const sortedPromos = [...res.data.promos].sort((a, b) => {
            return (a.id || 0) - (b.id || 0);
          });
          setDetail({
            ...res.data,
            promos: sortedPromos
          });
        } else {
          setDetail(res.data);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setLoadingState(false);
    }
  }, [id, setLoading]);

  useEffect(() => {
    if (id) fetchDetail();
  }, [id, fetchDetail]);

  const handleBack = () => {
    history.push("/cineclub/eshop/coupons");
  };

  const getStatusColor = (assigned) => {
    return assigned ? "tertiary" : "primary";
  };

  const getStatusLabel = (assigned) => {
    return assigned ? "Assigned" : "Available";
  };

  const handleStatusChange = async (promoId, newStatus) => {
    try {
      setLoading(true);
      const response = await apiHelper.cineclub.updateProductPromo(promoId, {
        status: newStatus === 1,
        isDeleted: false,
      });

      if (response.data && response.data.code === 0) {
        // Reload coupon data to reflect latest stock
        await fetchDetail();
      } else {
        alert("Failed to update promo status. Please try again.");
      }
    } catch (error) {
      console.error("Error updating promo status:", error);
      alert("An error occurred while updating the promo status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (promoId, isAssigned) => {
    // Prevent deletion if promo is assigned
    if (isAssigned) {
      alert("Cannot delete an assigned promo. Please unassign it first.");
      return;
    }

    const decision = await modal.fire({
      title: "Delete Promo",
      text: "Are you sure you want to delete this promo? This action cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });

    if (decision.isConfirmed) {
      try {
        setLoading(true);
        const response = await apiHelper.cineclub.updateProductPromo(promoId, {
          isDeleted: true,
          status: false,
        });

        if (response.data && response.data.code === 0) {
          // Reload coupon data to reflect latest stock
          await fetchDetail();
          alert("Promo deleted successfully.");
        } else {
          alert("Failed to delete promo. Please try again.");
        }
      } catch (error) {
        console.error("Error deleting promo:", error);
        alert("An error occurred while deleting the promo. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Filter promos based on search term
  const filteredPromos = useMemo(() => {
    if (!detail?.promos) return [];
    if (!searchTerm.trim()) return detail.promos;

    const search = searchTerm.toLowerCase().trim();
    return detail.promos.filter(promo => 
      promo.promoCode?.toLowerCase().includes(search)
    );
  }, [detail?.promos, searchTerm]);

  // Reset page index when search term changes
  useEffect(() => {
    setPageIndex(0);
  }, [searchTerm]);

  // Pagination functions
  const gotoPage = useCallback((page) => {
    setPageIndex(page);
  }, []);

  const previousPage = useCallback(() => {
    setPageIndex(prev => Math.max(0, prev - 1));
  }, []);

  const nextPage = useCallback(() => {
    const maxPage = Math.max(0, Math.ceil((filteredPromos?.length || 0) / pageSize) - 1);
    setPageIndex(prev => Math.min(maxPage, prev + 1));
  }, [filteredPromos?.length, pageSize]);

  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < Math.ceil((filteredPromos?.length || 0) / pageSize) - 1;

  // Update paginated promos when filtered promos or pagination changes
  useEffect(() => {
    if (filteredPromos) {
      const startIndex = pageIndex * pageSize;
      const endIndex = startIndex + pageSize;
      const paginated = filteredPromos.slice(startIndex, endIndex);
      setPaginatedPromos(paginated);
    }
  }, [filteredPromos, pageIndex, pageSize]);

  return (
    <div className={classes.content}>
      <Box className={classes.header}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Coupons
        </Button>
        <Typography variant="h4">Coupon Details</Typography>
      </Box>

      <Paper className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Basic Info
        </Typography>
        {loading && <Typography>Loading...</Typography>}
        {!loading && detail && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} className={classes.row}>
              <span className={classes.label}>ID:</span>
              <span>{detail.id}</span>
            </Grid>
            <Grid item xs={12} md={6} className={classes.row}>
              <span className={classes.label}>Stock:</span>
              <span>{detail.stock}</span>
            </Grid>
            <Grid item xs={12} md={6} className={classes.row}>
              <span className={classes.label}>Title (EN):</span>
              <span>{detail.titleEn}</span>
            </Grid>
            <Grid item xs={12} md={6} className={classes.row}>
              <span className={classes.label}>Title (TC):</span>
              <span>{detail.titleTc}</span>
            </Grid>
            <Grid item xs={12} md={6} className={classes.row}>
              <span className={classes.label}>Description (EN):</span>
              <span dangerouslySetInnerHTML={{ __html: detail.descriptionEn || "-" }} />
            </Grid>
            <Grid item xs={12} md={6} className={classes.row}>
              <span className={classes.label}>Description (TC):</span>
              <span dangerouslySetInnerHTML={{ __html: detail.descriptionTc || "-" }} />
            </Grid>
            <Grid item xs={12} md={6} className={classes.row}>
              <span className={classes.label}>Valid From:</span>
              <span>{formatDate(detail.validFrom)}</span>
            </Grid>
            <Grid item xs={12} md={6} className={classes.row}>
              <span className={classes.label}>Expiry Date:</span>
              <span>{formatDate(detail.expiryDate)}</span>
            </Grid>
            <Grid item xs={12} md={6} className={classes.row}>
              <span className={classes.label}>Point Price:</span>
              <span>{detail.pointPrice}</span>
            </Grid>
          </Grid>
        )}
      </Paper>

      <Paper className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Promo Codes
        </Typography>
        {detail && Array.isArray(detail.promos) && detail.promos.length > 0 ? (
          <>
            <Box className={classes.searchBox}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by promo code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {filteredPromos.length > 0 ? (
              <>
                <TableContainer className={classes.tableContainer}>
                  <Table size="small">
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell className={classes.tableHeaderCell}>Promo Code</TableCell>
                        <TableCell className={classes.tableHeaderCell}>Redeem Date</TableCell>
                        <TableCell className={classes.tableHeaderCell}>Assigned</TableCell>
                        <TableCell className={classes.tableHeaderCell}>Status</TableCell>
                        <TableCell className={classes.tableHeaderCell}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedPromos.map((promo) => (
                        <TableRow key={promo.id}>
                          <TableCell className={classes.tableCell}>
                            <Typography 
                              className={classes.promoCode}
                              variant="body2"
                              style={{ fontSize: "0.875rem" }}
                            >
                              {promo.promoCode}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            {promo.redeemDate ? formatDate(promo.redeemDate) : '-'}
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <Chip
                              label={getStatusLabel(promo.assigned)}
                              color={getStatusColor(promo.assigned)}
                              size="small"
                              className={classes.statusChip}
                            />
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <Select
                              value={promo.status ? 1 : 0}
                              onChange={(e) => handleStatusChange(promo.id, e.target.value)}
                              className={classes.statusSelect}
                              variant="outlined"
                              size="small"
                            >
                              <MenuItem value={1}>Active</MenuItem>
                              <MenuItem value={0}>Inactive</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <IconButton
                              onClick={() => handleDelete(promo.id, promo.assigned)}
                              className={classes.deleteButton}
                              size="small"
                              aria-label="delete"
                              disabled={promo.assigned}
                              title={promo.assigned ? "Cannot delete assigned promo" : "Delete promo"}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Bar */}
                {filteredPromos.length > pageSize && (
                  <PaginationBar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    gotoPage={gotoPage}
                    previousPage={previousPage}
                    nextPage={nextPage}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageIndex={pageIndex}
                    totalCount={filteredPromos.length}
                  />
                )}
              </>
            ) : (
              <Typography color="textSecondary" style={{ marginTop: 16 }}>
                No promos found matching "{searchTerm}"
              </Typography>
            )}
          </>
        ) : (
          <Typography color="textSecondary">No promo codes.</Typography>
        )}
      </Paper>
    </div>
  );
}
